import { datadogRum } from '@datadog/browser-rum'
import { getCanaryStatus } from '@thyme/libs/src/version/version'
import { ButtonSize } from '@thyme/nashville/src/types/buttons'
import { DialogSize } from '@thyme/nashville/src/types/dialogs'
import every from 'lodash-es/every'
import { storeToRefs } from 'pinia'
import { computed, ref, SetupContext } from 'vue'
import { useRoute } from 'vue-router'
import { useNotificationStore } from '@/legacy/store/modules/notification'
import { NotificationType } from '@/legacy/types/notifications'
import {
  ReportIssueForm,
  reportIssueTypesForRouteMap,
  DEFAULT_DEBUG_STEPS,
} from '@/legacy/types/reporting'
import { useFlagStore } from '@/store/flags/flags'
import { useWhoAmI } from '@/store/profile/whoAmI'
import { DEPLOY_VERSION } from '@/version'
import { reportIssueToSlack } from '../queries/reportIssueToSlack'

/**
 *
 * Setup function for ReportIssueModal.vue
 * @param props
 * @param context
 */
export default function (props: any, context: SetupContext) {
  const selectedIssueType = ref('')
  const route = useRoute()
  const { showInformationBanner, includeCanaryInReportIssue } = storeToRefs(
    useFlagStore()
  )

  /**
   * Returns debug steps to display in the report issue modal.
   * If showInformationBanner is true, includes an additional step to confirm the issue is not a known issue.
   * IMPROVEME(MT-5079): make this dynamic based on selectedIssueType
   */
  const debugCheckboxOptions = computed(() => {
    if (showInformationBanner.value) {
      return [
        {
          label: 'My issue is not highlighted above as a known issue',
        },
        ...DEFAULT_DEBUG_STEPS,
      ]
    }
    return DEFAULT_DEBUG_STEPS
  })

  const checkedDebugStepsMap = debugCheckboxOptions.value.reduce(
    (acc: { [key: string]: boolean }, step) => {
      acc[step.label] = false
      return acc
    },
    {}
  )

  const dirtyValues = ref<ReportIssueForm>(emptyForm())

  const close = () => {
    context.emit('close')
  }

  const issueTypeOptions = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return reportIssueTypesForRouteMap[route.name!.toString()] ?? ['Other']
  })

  const { selfEntity } = useWhoAmI()

  /**
   * Sends a metric to Datadog for the specific page and issue type,
   * used to track the number of issues reported by users and page if threshold met
   * @param data
   */
  async function reportIssue(data: ReportIssueForm) {
    try {
      datadogRum.addAction('reportIssueFromTB', {
        route: String(route.name),
        issueType: data.issueType,
      })
    } catch (err) {
      console.error('Failed to report issue to Datadog', err)
    }
  }

  /**
   * Sends request body to /reporting/thymebox_issue
   * Which posts to #thymebox-reported-issues Slack channel
   * @param data
   */
  async function sendToSlack(data: ReportIssueForm) {
    try {
      data.route = String(route.name)

      if (data.route.includes('#patientProfile')) {
        data.fullUrl = window.location.href
      } else {
        data.fullUrl = ''
      }

      if (selfEntity.value && selfEntity.value.person) {
        data.reporterId = selfEntity.value.entityId
        data.reporter = `${selfEntity.value.person.firstName} ${selfEntity.value.person.lastName}`
      }

      // IMPROVE (EE-172): Remove this if statement and do this always
      if (includeCanaryInReportIssue.value) {
        data.uiVersion = DEPLOY_VERSION
        data.canaryStatus = await getCanaryStatus()
      }

      await reportIssueToSlack(data, includeCanaryInReportIssue.value)

      useNotificationStore().setNotification({
        message: 'Successfully reported issue',
        type: NotificationType.SUCCESS,
      })
    } catch {
      useNotificationStore().setNotification({
        message: 'Failed to report issue',
        type: NotificationType.WARNING,
      })
    }
  }

  const isSaving = ref(false)

  /**
   * Returns true if form can be submitted, meaning all required fields are filled, false otherwise.
   */
  const canSubmit = computed(() => {
    return (
      !isSaving.value && // not currently saving
      !!dirtyValues.value.issueType && // issueType is required
      !!dirtyValues.value.description && // description is required
      every(dirtyValues.value.checkedDebugSteps, (value) => !!value) // all debug steps checked
    )
  })

  /**
   *
   * @param data
   */
  async function submit(data: ReportIssueForm) {
    isSaving.value = true
    await reportIssue(data)
    await sendToSlack(data)
    close()
    isSaving.value = false
  }

  /**
   *
   * Function called to return intial values for form
   *
   */
  function emptyForm(): ReportIssueForm {
    return {
      route: '',
      issueType: '',
      fullUrl: '',
      description: '',
      reporter: '',
      reporterId: '',
      checkedDebugSteps: checkedDebugStepsMap,
    }
  }

  return {
    ButtonSize,
    DialogSize,
    selectedIssueType,
    dirtyValues,
    route,
    issueTypeOptions,
    selfEntity,
    debugCheckboxOptions,
    canSubmit,
    submit,
    reportIssue,
    sendToSlack,
    close,
    isSaving,
  }
}
